/**
 * Google Drive REST API v3 wrapper.
 *
 * Uses the `drive.file` scope, which grants access only to files this app
 * creates (identified by the same OAuth client ID), making it safe across
 * devices without requiring broader Drive permissions.
 *
 * Folder ID and per-file Drive IDs are cached in localStorage to minimise
 * repeated list calls.
 */

const DRIVE_API_BASE = 'https://www.googleapis.com/drive/v3';
const DRIVE_UPLOAD_BASE = 'https://www.googleapis.com/upload/drive/v3';
export const DRIVE_FOLDER_NAME = 'mini-fridge';

const LS_FOLDER_ID_KEY = 'frostgrave:drive-folder-id';
const LS_FILE_IDS_KEY = 'frostgrave:drive-file-ids';
const LS_MODIFIED_TIMES_KEY = 'frostgrave:drive-modified-times';
let activeAccountId: string | null = null;

export const getDriveAccountStorageKey = (key: string) =>
  activeAccountId ? `${key}:${activeAccountId}` : key;

export function setDriveAccountId(accountId: string | null): void {
  activeAccountId = accountId;
}

export interface DriveFile {
  id: string;
  name: string;
  modifiedTime?: string;
  version?: string;
}

export class DriveApiError extends Error {
  constructor(public readonly status: number, message: string) {
    super(message);
    this.name = 'DriveApiError';
  }
}

export interface DriveJsonDocument<T> {
  content: T;
  /** ETag when exposed by CORS, otherwise a `drive-version:<n>` token. */
  etag: string | null;
}

const DRIVE_VERSION_TOKEN_PREFIX = 'drive-version:';

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

async function driveRequest(
  url: string,
  accessToken: string,
  options?: RequestInit
): Promise<Response> {
  const response = await fetch(url, {
    ...options,
    headers: {
      Authorization: `Bearer ${accessToken}`,
      ...(options?.headers ?? {}),
    },
  });
  if (!response.ok) {
    const errorText = await response.text().catch(() => response.statusText);
    throw new DriveApiError(response.status, `Drive API ${response.status}: ${errorText}`);
  }
  return response;
}

// ---------------------------------------------------------------------------
// Folder-ID cache
// ---------------------------------------------------------------------------

function getCachedFolderId(): string | null {
  return localStorage.getItem(getDriveAccountStorageKey(LS_FOLDER_ID_KEY));
}

function setCachedFolderId(id: string): void {
  localStorage.setItem(getDriveAccountStorageKey(LS_FOLDER_ID_KEY), id);
}

function clearCachedFolderId(): void {
  localStorage.removeItem(getDriveAccountStorageKey(LS_FOLDER_ID_KEY));
}

// ---------------------------------------------------------------------------
// File-ID cache  (maps logical key → Drive file ID)
// Logical keys: 'index' for warbands-index.json, warband ID for each warband.
// ---------------------------------------------------------------------------

export function getFileIdCache(): Record<string, string> {
  try {
    const raw = localStorage.getItem(getDriveAccountStorageKey(LS_FILE_IDS_KEY));
    return raw ? (JSON.parse(raw) as Record<string, string>) : {};
  } catch {
    return {};
  }
}

export function setFileIdCache(cache: Record<string, string>): void {
  localStorage.setItem(getDriveAccountStorageKey(LS_FILE_IDS_KEY), JSON.stringify(cache));
}

export function clearDriveCache(): void {
  clearCachedFolderId();
  localStorage.removeItem(getDriveAccountStorageKey(LS_FILE_IDS_KEY));
  localStorage.removeItem(getDriveAccountStorageKey(LS_MODIFIED_TIMES_KEY));
}

export function getDriveModifiedTimeCache(): Record<string, string> {
  try {
    const raw = localStorage.getItem(getDriveAccountStorageKey(LS_MODIFIED_TIMES_KEY));
    return raw ? (JSON.parse(raw) as Record<string, string>) : {};
  } catch {
    return {};
  }
}

export function setDriveModifiedTimeCache(cache: Record<string, string>): void {
  localStorage.setItem(getDriveAccountStorageKey(LS_MODIFIED_TIMES_KEY), JSON.stringify(cache));
}

// ---------------------------------------------------------------------------
// findOrCreateFolder
// ---------------------------------------------------------------------------

export async function findOrCreateFolder(accessToken: string): Promise<string> {
  const cached = getCachedFolderId();
  if (cached) {
    // Verify the folder is still accessible
    try {
      await driveRequest(
        `${DRIVE_API_BASE}/files/${cached}?fields=id`,
        accessToken
      );
      return cached;
    } catch {
      clearCachedFolderId();
    }
  }

  // Search within app-accessible files
  const query = encodeURIComponent(
    `name='${DRIVE_FOLDER_NAME}' and mimeType='application/vnd.google-apps.folder' and trashed=false`
  );
  const listRes = await driveRequest(
    `${DRIVE_API_BASE}/files?q=${query}&fields=files(id,name)`,
    accessToken
  );
  const listData = (await listRes.json()) as { files: DriveFile[] };

  if (listData.files.length > 0) {
    const folderId = listData.files[0].id;
    setCachedFolderId(folderId);
    return folderId;
  }

  // Create the folder
  const createRes = await driveRequest(`${DRIVE_API_BASE}/files?fields=id`, accessToken, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      name: DRIVE_FOLDER_NAME,
      mimeType: 'application/vnd.google-apps.folder',
    }),
  });
  const created = (await createRes.json()) as DriveFile;
  setCachedFolderId(created.id);
  return created.id;
}

// ---------------------------------------------------------------------------
// listFilesInFolder
// ---------------------------------------------------------------------------

export async function listFilesInFolder(
  accessToken: string,
  folderId: string
): Promise<DriveFile[]> {
  const query = encodeURIComponent(
    `'${folderId}' in parents and trashed=false`
  );
  const files: DriveFile[] = [];
  let pageToken: string | undefined;
  do {
    const pageTokenQuery = pageToken ? `&pageToken=${encodeURIComponent(pageToken)}` : '';
    const res = await driveRequest(
      `${DRIVE_API_BASE}/files?q=${query}&pageSize=1000&fields=nextPageToken,files(id,name,modifiedTime)${pageTokenQuery}`,
      accessToken
    );
    const data = (await res.json()) as { files: DriveFile[]; nextPageToken?: string };
    files.push(...data.files);
    pageToken = data.nextPageToken;
  } while (pageToken);
  return files;
}

// ---------------------------------------------------------------------------
// uploadFile — creates a new file or updates an existing one
// ---------------------------------------------------------------------------

export async function uploadFile(
  accessToken: string,
  folderId: string,
  fileName: string,
  content: unknown,
  existingFileId?: string
): Promise<string> {
  const jsonBody = JSON.stringify(content);
  const contentType = 'application/json';

  if (existingFileId) {
    const res = await driveRequest(
      `${DRIVE_UPLOAD_BASE}/files/${existingFileId}?uploadType=media&fields=id`,
      accessToken,
      {
        method: 'PATCH',
        headers: { 'Content-Type': contentType },
        body: jsonBody,
      }
    );
    const data = (await res.json()) as DriveFile;
    return data.id;
  }

  // Multipart upload to set metadata (name, parent) and content in one request
  const boundary = 'fg_drive_boundary';
  const metadata = JSON.stringify({ name: fileName, parents: [folderId] });
  const multipart = [
    `--${boundary}`,
    'Content-Type: application/json; charset=UTF-8',
    '',
    metadata,
    `--${boundary}`,
    `Content-Type: ${contentType}`,
    '',
    jsonBody,
    `--${boundary}--`,
  ].join('\r\n');

  const res = await driveRequest(
    `${DRIVE_UPLOAD_BASE}/files?uploadType=multipart&fields=id`,
    accessToken,
    {
      method: 'POST',
      headers: {
        'Content-Type': `multipart/related; boundary=${boundary}`,
      },
      body: multipart,
    }
  );
  const data = (await res.json()) as DriveFile;
  return data.id;
}

// ---------------------------------------------------------------------------
// downloadFile
// ---------------------------------------------------------------------------

export async function downloadFile(
  accessToken: string,
  fileId: string
): Promise<unknown> {
  const res = await driveRequest(
    `${DRIVE_API_BASE}/files/${fileId}?alt=media`,
    accessToken
  );
  return res.json();
}

export async function downloadJsonWithEtag<T>(
  accessToken: string,
  fileId: string
): Promise<DriveJsonDocument<T>> {
  const response = await driveRequest(
    `${DRIVE_API_BASE}/files/${fileId}?alt=media`,
    accessToken
  );
  const content = (await response.json()) as T;
  const responseEtag = response.headers.get('etag');
  if (responseEtag) {
    return { content, etag: responseEtag };
  }

  const metadataResponse = await driveRequest(
    `${DRIVE_API_BASE}/files/${fileId}?fields=version`,
    accessToken
  );
  const metadata = (await metadataResponse.json()) as Pick<DriveFile, 'version'>;
  return {
    content,
    etag: metadata.version
      ? `${DRIVE_VERSION_TOKEN_PREFIX}${metadata.version}`
      : null,
  };
}

export async function updateJsonConditionally(
  accessToken: string,
  fileId: string,
  content: unknown,
  expectedEtag: string
): Promise<string> {
  let conditionalHeaders: Record<string, string> = { 'If-Match': expectedEtag };
  if (expectedEtag.startsWith(DRIVE_VERSION_TOKEN_PREFIX)) {
    const expectedVersion = expectedEtag.slice(DRIVE_VERSION_TOKEN_PREFIX.length);
    const metadataResponse = await driveRequest(
      `${DRIVE_API_BASE}/files/${fileId}?fields=version`,
      accessToken
    );
    const metadata = (await metadataResponse.json()) as Pick<DriveFile, 'version'>;
    if (!metadata.version || String(metadata.version) !== expectedVersion) {
      throw new DriveApiError(412, 'Drive manifest changed before it could be updated');
    }
    conditionalHeaders = {};
  }

  const response = await driveRequest(
    `${DRIVE_UPLOAD_BASE}/files/${fileId}?uploadType=media&fields=id`,
    accessToken,
    {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        ...conditionalHeaders,
      },
      body: JSON.stringify(content),
    }
  );
  const data = (await response.json()) as DriveFile;
  return data.id;
}
